import React, { useCallback, type MouseEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import Button from '@atlaskit/button/standard-button';
import UnlinkIcon from '@atlaskit/icon/core/migration/link-broken--unlink';
import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

type Props = {
	onClick: (analyticsEvent: UIAnalyticsEvent) => void;
	label?: string;
};

const UnlinkButton = ({ onClick, label }: Props) => {
	const { formatMessage } = useIntl();

	const onClickCallback = useCallback(
		(event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			event.stopPropagation();
			fireUIAnalytics(analyticsEvent, 'dependencyUnlinkButton');
			onClick(analyticsEvent);
		},
		[onClick],
	);

	const unlinkMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
		? messages.unlinkIssueTermRefresh
		: messages.unlink;

	const unlinkItemMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
		? messages.unlinkItemIssueTermRefresh
		: messages.unlinkItem;

	return fg('jsw_roadmaps_timeline-post-project-a11y-fix') ? (
		<Tooltip content={formatMessage(messages.unlink)}>
			{(tooltipProps) => (
				<IconButton
					{...tooltipProps}
					label={
						label
							? formatMessage(unlinkItemMessage, { issue: label })
							: formatMessage(unlinkMessage)
					}
					icon={UnlinkIcon}
					testId="roadmap.timeline-table-kit.common.ui.dependency.unlink-button.unlink-button"
					spacing="compact"
					appearance="subtle"
					onClick={onClickCallback}
					{...(fg('platform_button_item-add-ufo-metrics')
						? { interactionName: 'timeline-dependency-unlink-clicked' }
						: {})}
				/>
			)}
		</Tooltip>
	) : (
		<Tooltip content={formatMessage(messages.unlink)}>
			<Button
				iconBefore={
					<UnlinkIcon
						LEGACY_size="small"
						label={formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.unlinkIssueTermRefresh
								: messages.unlink,
						)}
						testId="roadmap.timeline-table-kit.common.ui.dependency.unlink-button.unlink-button"
						color="currentColor"
					/>
				}
				spacing="compact"
				appearance="subtle"
				onClick={onClickCallback}
			/>
		</Tooltip>
	);
};

export default UnlinkButton;
